import React from 'react';
import { motion } from 'motion/react';
import { Target, Heart, Shield, Sparkles, Users, Award } from 'lucide-react';
import Seo from '../components/Seo';
import { PAGE_METADATA, getAboutStructuredData } from '../seo/pageMetadata';

const About = () => {
  const values = [
    {
      title: 'Integrity',
      desc: 'We operate with absolute transparency and honesty in all our initiatives.',
      icon: <Shield className="w-6 h-6" />,
    },
    {
      title: 'Innovation',
      desc: 'We encourage creative solutions and forward-thinking business models.',
      icon: <Sparkles className="w-6 h-6" />,
    },
    {
      title: 'Community',
      desc: 'We believe in the power of collective growth and mutual support.',
      icon: <Users className="w-6 h-6" />,
    },
    {
      title: 'Excellence',
      desc: 'We strive for the highest standards in everything we deliver.',
      icon: <Award className="w-6 h-6" />,
    },
  ];

  return (
    <>
      <Seo {...PAGE_METADATA.about} structuredData={getAboutStructuredData()} />
      <div className="pt-32 pb-24">
        <section className="px-6 max-w-7xl mx-auto text-center mb-24">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-royal-accent border border-amber-200 px-4 py-2 rounded-full mb-6">
              <Target className="w-4 h-4 text-royal-purple" />
              <span className="text-royal-purple text-[10px] font-black uppercase tracking-[0.2em]">Our Story</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-display font-black text-royal-dark tracking-tighter mb-8">
              Beauty with <br />
              <span className="text-royal-purple">Purpose & Vision</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed font-medium">
              Delight Business Empowerment was born from a simple yet powerful realization: that beauty is most impactful when
              paired with intelligence and a drive for social change. We exist to celebrate and empower the next
              generation of female leaders.
            </p>
          </motion.div>
        </section>

        <section className="px-6 max-w-7xl mx-auto mb-32">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-royal-dark p-12 rounded-[3rem] text-white">
              <h2 className="text-3xl font-display font-black mb-6 text-royal-gold">Our Mission</h2>
              <p className="text-royal-accent/80 text-lg leading-relaxed font-medium">
                To provide a prestigious platform where young women can showcase their talents, gain confidence, and
                access the resources and mentorship needed to become impactful leaders in their communities.
              </p>
            </div>
            <div className="bg-royal-accent p-12 rounded-[3rem] border border-amber-200">
              <h2 className="text-3xl font-display font-black mb-6 text-royal-purple">Our Vision</h2>
              <p className="text-slate-700 text-lg leading-relaxed font-medium">
                To redefine beauty standards by prioritizing intelligence, character, and social impact, ultimately
                nurturing a global network of empowered women who lead with grace and purpose.
              </p>
            </div>
          </div>
        </section>

        <section className="px-6 max-w-7xl mx-auto mb-32">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-display font-black text-royal-dark mb-4">Our Core Values</h2>
            <p className="text-slate-500 font-medium">The principles that guide every decision we make.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-xl shadow-black/5 text-center group hover:border-royal-purple/20 transition-all"
              >
                <div className="w-14 h-14 bg-royal-accent rounded-2xl flex items-center justify-center text-royal-purple mx-auto mb-6 group-hover:scale-110 transition-transform">
                  {value.icon}
                </div>
                <h3 className="text-xl font-display font-black text-royal-dark mb-3">{value.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed font-medium">{value.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="px-6 max-w-5xl mx-auto bg-slate-50 rounded-[4rem] p-16 text-center">
          <Heart className="w-12 h-12 text-royal-purple mx-auto mb-8" />
          <h2 className="text-3xl md:text-4xl font-display font-black text-royal-dark mb-8">Why Delight Business Empowerment?</h2>
          <p className="text-slate-600 text-xl leading-relaxed font-medium">
            We understand that behind every business is a dream, and behind every dream is a person striving for a
            better future. Our platform is more than just a resource hub and it is a support system designed to ensure
            that effort, creativity, and determination are recognized and rewarded.
          </p>
        </section>
      </div>
    </>
  );
};

export default About;
